import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { LandingSitePublishedFormsDtoOut } from '@/models/out/LandingSitePublishedFormsDtoOut';
import { downloadFileFromApi, openFileFromApi } from '@/helpers/downloadFile';
import { PUBLIC_DETAILS_PAGES } from '@/pages/ManagementArea/LandingSiteForms/landingSiteForms';
import ShareableLinks from '@/pages/Donation/DonationsAdmin/ShareableLinks';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import eyeIcon from '@/assets/icons/eye.svg';
import copyIcon from '@/assets/icons/copy.svg';
import doneIcon from '@/assets/icons/done.svg';
import DocumentRow from './DocumentRow';
import DonorDetailsBody, { DonorDetailsKey } from './DonorDetailsBody';
import detailsStyles from './DonorDetails.module.css';
import styles from '../FundSettings.module.css';

/** The catalogue entries that are particulars in a popup, in the order a coordinator offers them. */
const DETAILS_ENTRIES: DonorDetailsKey[] = ['bank-details-israel', 'bank-details-abroad', 'check-deposit'];

interface DonorKitSectionProps {
  fundId: number;
  forms: LandingSitePublishedFormsDtoOut | undefined;
}

/**
 * «לתורם: איך תורמים לקרן» (HAT-1231): everything a coordinator hands a donor, every item already
 * pointed at this fund.
 *
 * The fund's own donation page comes first, in place of the site's generic online forms: those ask
 * the donor to type a fund number by hand, and a mistyped number is a donation the office has to
 * place manually (HAT-1280). The bank and cheque particulars come from the same managed catalogue
 * the site reads, shown in the site's own layout - and the portal adds the one thing the public
 * site cannot know: which fund to name on the transfer.
 */
const DonorKitSection = ({ fundId, forms }: DonorKitSectionProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [openDetailsKey, setOpenDetailsKey] = useState<DonorDetailsKey | null>(null);
  const [copiedLinkKey, setCopiedLinkKey] = useState<string | null>(null);
  const [isDownloadingForm, setIsDownloadingForm] = useState(false);
  const [isOpeningForm, setIsOpeningForm] = useState(false);

  // Generated by the server per fund, with the fund's name and number printed on it, and served as
  // an attachment - so viewing it means fetching the bytes and handing them to a tab.
  const standingOrderFormUrl = `funds/financial-data/${fundId}/documents/standing-order-form`;

  const handleDownloadForm = async () => {
    if (isDownloadingForm) return;
    setIsDownloadingForm(true);
    try {
      await downloadFileFromApi(standingOrderFormUrl, `${t('fundSettingsDocumentsPage.standingOrderFormFileName')}-${fundId}.pdf`, currentUser?.token ?? '');
    } finally {
      setIsDownloadingForm(false);
    }
  };

  const handleViewForm = async () => {
    if (isOpeningForm) return;
    setIsOpeningForm(true);
    try {
      await openFileFromApi(standingOrderFormUrl, currentUser?.token ?? '');
    } finally {
      setIsOpeningForm(false);
    }
  };

  /** «קישור לתורם»: the page on the public site that shows exactly these particulars, copied. */
  const copyDonorLink = async (key: string, url: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedLinkKey(key);
    setTimeout(() => setCopiedLinkKey(null), 1800);
  };

  const detailsOf = (key: DonorDetailsKey) => forms?.details?.[key];
  const openDetails = openDetailsKey ? detailsOf(openDetailsKey) : undefined;

  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>{t('fundSettingsDocumentsPage.donorKitTitle')}</h2>
      <p className={styles.panelSubtitle}>{t('fundSettingsDocumentsPage.donorKitSubtitle', { fundId })}</p>

      <div className={styles.documentList}>
        <DocumentRow
          name={t('fundSettingsDocumentsPage.donationPageTitle')}
          note={t('fundSettingsDocumentsPage.donationPageNote')}
        >
          <ShareableLinks fundId={fundId} inline />
        </DocumentRow>

        {DETAILS_ENTRIES.map((key) => {
          if (!detailsOf(key)) return null;
          const publicPage = PUBLIC_DETAILS_PAGES[key];
          return (
            <DocumentRow
              key={key}
              name={t(`landingSiteForms.items.${key}`)}
              note={key === 'check-deposit' ? t('fundSettingsDocumentsPage.chequeReference', { fundId }) : t('fundSettingsDocumentsPage.transferReference', { fundId })}
            >
              <button type="button" className={styles.linkBtn} onClick={() => setOpenDetailsKey(key)}>
                <img src={eyeIcon} alt="" className={styles.actionIcon} />
                {t('fundSettingsDocumentsPage.viewDetails')}
              </button>
              {publicPage && (
                <button type="button" className={styles.linkBtn} onClick={() => copyDonorLink(key, publicPage)} aria-live="polite">
                  <img src={copiedLinkKey === key ? doneIcon : copyIcon} alt="" className={styles.actionIcon} />
                  {copiedLinkKey === key ? t('fundSettingsDocumentsPage.details.linkCopied') : t('fundSettingsDocumentsPage.donorLink')}
                </button>
              )}
            </DocumentRow>
          );
        })}

        <DocumentRow
          name={t('fundSettingsDocumentsPage.standingOrderFormTitle')}
          note={t('fundSettingsDocumentsPage.standingOrderFormSubtitle')}
          onView={handleViewForm}
          onDownload={handleDownloadForm}
          isBusy={isOpeningForm || isDownloadingForm}
        />
      </div>

      <p className={styles.panelSubtitle}>
        {t('fundSettingsDocumentsPage.receiptsHint')}{' '}
        <Link to={`/${fundId}/transactions`} className={styles.factLink}>{t('transactionsBoard')}</Link>
      </p>

      <MainDialogLayout
        open={!!openDetailsKey}
        onOpenChange={(open: boolean) => { if (!open) setOpenDetailsKey(null); }}
        title={openDetailsKey ? t(`landingSiteForms.items.${openDetailsKey}`) : ''}
        maxWidth="680px"
        fitContent
        contentClassName={detailsStyles.dialog}
      >
        {openDetailsKey && openDetails && (
          <DonorDetailsBody entryKey={openDetailsKey} details={openDetails} fundId={fundId} publicPageUrl={PUBLIC_DETAILS_PAGES[openDetailsKey]} />
        )}
      </MainDialogLayout>
    </div>
  );
};

export default DonorKitSection;
